import { z } from "zod";
import {
  DND_5E_CORE_ID,
  MAX_CAMPAIGN_SYSTEM_INSTRUCTIONS_LENGTH,
} from "@/lib/constants";
import { CampaignSchema } from "@/lib/core/schemas/campaign";
import {
  CharacterCreationChoiceSummaryDnd5eSchema,
  CharacterCreationDraftDnd5eSchema,
  CharacterCreationInventoryItemDnd5eSchema,
  CharacterCreationOpPlanDnd5eSchema,
  CharacterCreationPreviewProvenanceDnd5eSchema,
  CharacterCreationSelectedChoiceReceiptDnd5eSchema,
} from "@/lib/core/schemas/character-creation";
import { CharacterSheetDnd5eSchema } from "@/lib/core/schemas/character-sheet";

export const ACCOUNT_CHARACTER_PAYLOAD_VERSION = 1;
export const ACCOUNT_CHARACTER_PLAN_VERSION = 1;
export const ACCOUNT_CHARACTER_RULESET_KEY = "dnd5e" as const;

export const AccountCharacterRulesetKeySchema = z.literal(
  ACCOUNT_CHARACTER_RULESET_KEY
);
export type AccountCharacterRulesetKey = z.infer<
  typeof AccountCharacterRulesetKeySchema
>;

export const StoredCharacterCreationPreviewDnd5eSchema = z.object({
  sheet: CharacterSheetDnd5eSchema,
  inventory: z.array(CharacterCreationInventoryItemDnd5eSchema).readonly(),
  choiceSummary: z.array(CharacterCreationChoiceSummaryDnd5eSchema).readonly(),
  provenance: z.array(CharacterCreationPreviewProvenanceDnd5eSchema).readonly(),
});
export type StoredCharacterCreationPreviewDnd5e = z.infer<
  typeof StoredCharacterCreationPreviewDnd5eSchema
>;

export const AccountCharacterCompatibilitySchema = z.object({
  payloadVersion: z.number().int().positive(),
  compilerVersion: z.string().min(1),
  planVersion: z.number().int().positive(),
});
export type AccountCharacterCompatibility = z.infer<
  typeof AccountCharacterCompatibilitySchema
>;

export const AccountCharacterAssignmentStatusSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({ kind: z.literal("available") }),
    z.object({
      kind: z.literal("assigned"),
      campaignId: z.string().uuid(),
      campaignName: z.string().min(1),
      campaignArchived: z.boolean(),
      actorId: z.string().min(1),
      assignedAt: z.string().datetime(),
    }),
  ]
);
export type AccountCharacterAssignmentStatus = z.infer<
  typeof AccountCharacterAssignmentStatusSchema
>;

export const AccountCharacterSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  rulesetId: z.string().uuid(),
  rulesetKey: AccountCharacterRulesetKeySchema,
  level: z.literal(1),
  createdAt: z.string().datetime(),
  compatibility: AccountCharacterCompatibilitySchema,
  preview: StoredCharacterCreationPreviewDnd5eSchema,
  status: AccountCharacterAssignmentStatusSchema,
});
export type AccountCharacterSummary = z.infer<
  typeof AccountCharacterSummarySchema
>;

export const AccountCharacterDetailSchema = AccountCharacterSummarySchema;
export type AccountCharacterDetail = AccountCharacterSummary;

export const CreateAccountCharacterRecordSchema = z.object({
  id: z.string().uuid(),
  ownerId: z.string().min(1),
  rulesetId: z.string().uuid(),
  rulesetKey: AccountCharacterRulesetKeySchema,
  level: z.literal(1),
  payloadVersion: z.literal(ACCOUNT_CHARACTER_PAYLOAD_VERSION),
  compilerVersion: z.string().min(1),
  planVersion: z.literal(ACCOUNT_CHARACTER_PLAN_VERSION),
  normalizedDraft: CharacterCreationDraftDnd5eSchema,
  selectedChoiceReceipts: z
    .array(CharacterCreationSelectedChoiceReceiptDnd5eSchema)
    .readonly(),
  plan: CharacterCreationOpPlanDnd5eSchema,
  planDigest: z.string().min(1),
  confirmationRequestDigest: z.string().min(1),
  preview: StoredCharacterCreationPreviewDnd5eSchema,
});
export type CreateAccountCharacterRecord = z.infer<
  typeof CreateAccountCharacterRecordSchema
>;

export const AccountCharacterAssignmentRecordSchema = z.object({
  characterId: z.string().uuid(),
  campaignId: z.string().uuid(),
  actorId: z.string().min(1),
  assignedAt: z.date(),
});
export type AccountCharacterAssignmentRecord = z.infer<
  typeof AccountCharacterAssignmentRecordSchema
>;

export const CreateAccountCharacterAssignmentInputSchema =
  AccountCharacterAssignmentRecordSchema.omit({ assignedAt: true });
export type CreateAccountCharacterAssignmentInput = z.infer<
  typeof CreateAccountCharacterAssignmentInputSchema
>;

export const AccountCharacterLaunchRecordSchema =
  CreateAccountCharacterRecordSchema.extend({
    createdAt: z.date(),
    assignment: AccountCharacterAssignmentRecordSchema.nullable(),
  });
export type AccountCharacterLaunchRecord = z.infer<
  typeof AccountCharacterLaunchRecordSchema
>;

export const CreateAccountCharacterDnd5eInputSchema = z.object({
  rulesetId: z.literal(DND_5E_CORE_ID),
  draft: CharacterCreationDraftDnd5eSchema,
  expectedPlanDigest: z.string().min(1),
});
export type CreateAccountCharacterDnd5eInput = z.infer<
  typeof CreateAccountCharacterDnd5eInputSchema
>;

export const CreateAccountCharacterDnd5eResultSchema = z.object({
  character: AccountCharacterDetailSchema,
  replayed: z.boolean(),
});
export type CreateAccountCharacterDnd5eResult = z.infer<
  typeof CreateAccountCharacterDnd5eResultSchema
>;

export const LaunchCampaignWithCharacterDnd5eInputSchema = z.object({
  characterId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  systemInstructions: z
    .string()
    .max(MAX_CAMPAIGN_SYSTEM_INSTRUCTIONS_LENGTH)
    .optional(),
});
export type LaunchCampaignWithCharacterDnd5eInput = z.infer<
  typeof LaunchCampaignWithCharacterDnd5eInputSchema
>;

export const LaunchCampaignWithCharacterDnd5eResultSchema = z.object({
  campaign: CampaignSchema,
  characterId: z.string().uuid(),
  actorId: z.string().min(1),
});
export type LaunchCampaignWithCharacterDnd5eResult = z.infer<
  typeof LaunchCampaignWithCharacterDnd5eResultSchema
>;
